<template>
	<Z-modal :hasEditableName="!isUnassigned" :name="thisTask.name" @editName="editNameAlertPrompt()" @closeClicked="closeModal()">
		<template #header>
			<div v-if="!isUnassigned" class="pin-icon">
				<img v-if="thisTask.pinned" src="../../_assets/push-pin.png" alt="img not found" @click="togglePinTask()" class="close-modal-icon">
				<img v-else src="../../_assets/push-pin-outline.png" alt="img not found" @click="togglePinTask()" class="close-modal-icon">
			</div>
		</template>

		<div class="content w-full h-full" @scroll="onScroll" ref="content">
			<h2 class="mt-1">Project:</h2>
			<ion-toolbar class="project-info flex ion-align-items-center mb-3" @click="openProjectsSelectModal()">
				<img v-if="projectChoice?.project_image" class="project-image mr-1" slot="start" :src="projectChoice.project_image" />
				<img v-else class="project-image mr-1" slot="start" src="@/plugins/app/_assets/wzo.jpg" />

				<h4 v-if="projectChoice">{{ projectChoice?.name ?? projectChoice.contactName }}</h4>

				<ion-icon v-if="!isUnassigned" :icon="$icons.listSharp" class="project-select-icon" slot="end" />
			</ion-toolbar>

			<template v-if="!isUnassigned">
				<div class="row w-full">
					<ion-icon aria-label="Favorite" v-if="missingCategoryWarning" :icon="$icons.alert" class="size-xxl text-danger mt-auto"/>
					<div class="categories relative w-full m-0">
						<ion-label class="label">Category:</ion-label>
						<ion-select @click="getCategories" :value="thisTask.category?._id" :key="thisTask.categories" placeholder="Select category" interface="popover" @ionChange="updateCategory" :class="{'border-danger': missingCategoryWarning}" ref="categories">
							<ion-select-option v-if="!thisTask.categories?.length" disabled>No categories found!</ion-select-option>
							<ion-select-option v-else v-for="category in thisTask.categories" :key="category" :value="category._id">{{category.name}}</ion-select-option>
						</ion-select>
						<ion-icon class="size-sm absolute clear-icon" @click="clearCategory" :icon="$icons.close" />
					</div>
					<ion-icon v-if="projectChoice?.is_billable && times?.length != 0" @click="switchBillableForAll()" :icon="$icons.logoUsd" :class="{ active: thisTask.globalBillable }" class="ml-1 billable p-[6px]" />
				</div>

				<div class="row w-full">
					<div class="tracked-time relative w-full">
						<ion-label class="label">Tracked time:</ion-label>
						<ion-input :value="Date.time(totalTime).format('k\'h\' mm\'m\'')" class="mb-0 ion-no-padding text-[18px]" readonly />
					</div>
					<div class="planned-time relative">
						<ion-label class="label">Planned time:</ion-label>
						<ion-input :value="Date.time(plannedTimeCopy ?? 0).format(`k'h' mm'm'`)" class="mt-0 mb-0 ion-no-padding text-[18px]" ref="planned-input" @keyup.enter="sendUpdatePlannedTime" @ionChange="updatePlannedTime" />
						<ion-icon class="pencil-icon" :icon="$icons.pencil" />
					</div>
				</div>

				<template v-if="thisTask.notionTask">
					<div class="row w-full">
						<div class="notion-tracked-time relative w-full">
							<ion-label class="label"><b>Notion</b> tracked time:</ion-label>
							<ion-input :value="Date.time(thisTask.notionTask.notion_total_time * 60).format('k\'h\' mm\'m\'')" class="mb-0 ion-no-padding text-[18px]" readonly />
						</div>
						<div class="notion-planned-time relative" v-memo="[thisTask.notionTask.notion_estimated_time]">
							<ion-label class="label"><b>Notion</b> planned time:</ion-label>
							<ion-input :value="Date.time(thisTask.notionTask.notion_estimated_time * 60).format('k\'h\' mm\'m\'')" class="mb-0 ion-no-padding text-[18px]" readonly />
						</div>
					</div>

					<div class="row w-full">
						<div class="notion-status relative w-full">
							<ion-label class="label"><b>Notion</b> status:</ion-label>
							<ion-select @click="getNotionStatuses" :value="thisTask.notionTask.notion_status?._id" :key="thisTask.notionTask.notion_statuses" @ionChange="updateNotionStatus" placeholder="Select status" interface="popover" ref="notion-status" class="notion-status">
								<ion-select-option v-if="!thisTask.notionTask.notion_statuses?.length" disabled>No statuses found!</ion-select-option>
								<ion-select-option v-else v-for="status in thisTask.notionTask.notion_statuses" :key="status" :value="status._id">{{ status.name }}</ion-select-option>
							</ion-select>
							<div class="notion-status-dot absolute radius-50" :style="`background: ${thisTask.notionTask.notion_status?.color}`" :key="thisTask.notionTask.notion_status?.color"></div>
						</div>
						<div class="notion-progress relative" v-memo="[thisTask.notionTask.notion_progress]">
							<ion-label class="label"><b>Notion</b> progress:</ion-label>
							<ion-select :value="thisTask.notionTask.notion_progress" @ionChange="updateNotionProgress" placeholder="Select notion progress" interface="popover" ref="notion-progress" class="notion-progress-select">
								<ion-select-option v-for="i in [0,10,20,30,40,50,60,70,80,90,100]" :key="i" :value="i">{{ i }}%</ion-select-option>
							</ion-select>
							<ion-icon class="pencil-icon" :icon="$icons.pencil" />
						</div>
					</div>
				</template>

				<div class="note w-100 mt-2">
					<ion-label class="label">Note:</ion-label>
					<div class="flex">
						<ion-textarea
							placeholder="Note"
							:auto-grow="true"
							:value="thisTask.note || ''"
							@ionChange="editedNote"
							@keyup.enter="saveNoteEdit"
						/>
						<ion-button class="ml-2 mt-auto" @click="saveEdit">Save</ion-button>
					</div>
					
				</div>
				
				<div class="flex w-100 ion-justify-content-between mt-2">
					<h2>All time records:</h2>
					<p class="add-button" @click="openAddTimeModal()">Add time</p>
				</div>

				<div>
					<Z-timeline :times="times" :timeFormat="{ task: null, noBillableToggle: !projectChoice?.is_billable }" @addTime="openAddTimeModal" @editTime="editTime" @deleteTime="deleteTime" class="w-full mb-1" v-memo=[times] />
					<ion-skeleton-text v-if="pendingTimes" animated />
				</div>
			</template>
		</div>

		<template #footer>
			<ion-button v-if="!isUnassigned && isCompletable && !thisTask.completed && !isActive"
				@click="toggleCompleteTask()" class="w-full complete-btn mb-1" color="light">
				<ion-label>Complete task</ion-label>
			</ion-button>

			<ion-button v-if="thisTask.completed" @click="toggleCompleteTask()" class="w-full"
				expand="block" color="secondary">Reopen task</ion-button>
			<ion-button v-else-if="isActive" class="w-full" @click="setTracking(false)">
				<ion-icon class="size-l mr-1" :icon="$icons.stopCircle" />
				{{ Date.time(getCurrentTrackedTime).format() }}
			</ion-button>
			<ion-button v-else class="w-full" @click="setTracking(true)">Start tracking {{isUnassigned ? " and claim" : ""}}</ion-button>
		</template>
	</Z-modal>
</template>

<script>
import TaskProjectSelect from './modal-task-project-select'
import ZTimeline from '../z-timeline.vue'
import { IonTextarea } from '@ionic/vue'

import openTimestampModal from './modal-time-stamp'

import { mapMutations, mapGetters, mapActions } from 'vuex'

import axios from '@/plugins/w/axios/models/axios'

const api = {
	pinTask: (taskId, isPinned) => axios.post_auth_data(`/tasks/${taskId}/${isPinned ? 'pin' : 'unpin'}`),
	loadTimes: (taskId, page = 1, limit = 25) => axios.get_auth(`/tasks/${taskId}/times`, { page, limit }),
	updateTask: (taskId, newData) => axios.patch_auth_data(`/tasks/${taskId}`, newData),
	addTime: (taskId, projectId, start, end, comment, billable) => axios.post_auth_data('/times', { taskId, projectId, start, end, comment, billable }),
	getProject: (projectId) => axios.get_auth_data(`/projects/${projectId}`),
	getCategories: (projectId) => axios.get_auth_data(`/projects/${projectId}/categories`),
	switchBillableForAll: (taskId, billable) => axios.put_auth_data(`/tasks/${taskId}/changeBillable`, { billable }),
	getNotionTask: (taskId) => axios.get_auth_data(`/notion_tasks/${taskId}`),
	getNotionStatuses: () => axios.get_auth_data(`/notion_statuses`),
}

export default {
	components: { ZTimeline, IonTextarea },
	
	props: {
		task: {
			type: Object,
			required: true,
		},

		project: {
			type: Object
		},

		isUnassigned: {
			type: Boolean,
			default: false
		},

		isCompletable: {
			type: Boolean,
			default: true
		}
	},

	data() {
		return {
			thisTask: this.task,
			times: [],
			pendingTimes: null,

			projectChoice: this.project,

			taskPinChanged: false,

			clickEvent: null,
			isPicker: false,
			plannedTime: this.task.plannedTime,
			plannedTimeCopy: this.task.plannedTime,

			noteTextArea: null,
		}
	},

	async mounted() {
		this.projectChoice ??= await api.getProject(this.thisTask.projectId)

		this.thisTask.globalBillable ??= true
		this.loadTimes(this.thisTask.timesCache || [])
		this.getCategories()
	
		if (this.thisTask.notionTask) {
			await this.getNotionStatuses()
			await this.getNotionTask()
		}
	},

	beforeUnmount() {
		this.thisTask.timesCache = this.times 
		if (this.taskPinChanged) this.$eventBus.emit(this.thisTask.pinned ? 'reloadPinnedTasks' : 'unpinned', this.thisTask)
	},

	computed: {
		...mapGetters('projectsStore', ['getCurrentTrackedTime', 'getActiveTask']),

		totalTime() {
			return this.isActive ? this.thisTask.totalTime + this.getCurrentTrackedTime : this.thisTask.totalTime
		},

		// progress() {
		// 	return this.totalTime / this.thisTask.plannedTime
		// },

		isActive() {
			return this.getActiveTask?._id == this.thisTask._id
		},

		missingCategoryWarning() {
			return this.thisTask.emptyCategoryAlert
		},

	},

	methods: {
		...mapMutations('projectsStore', ['taskProjectChanged']),

		...mapActions('projectsStore', ['startTracking', 'stopTrackingAction']),

		async onScroll() {
			if (this.$refs.content.scrollTop / (this.$refs.content.scrollHeight - this.$refs.content.clientHeight) >= 0.99)
				await this.loadTimes()
		},

		// confirmNotionProgress() {
		// 	this.$refs['notion-progress-input']?.$el.setBlur()
		// },

		// Pin Task

		async togglePinTask() {
			this.thisTask.pinned = !this.thisTask.pinned
			try {
				this.taskPinChanged = !this.taskPinChanged
				await api.pinTask(this.thisTask._id, this.thisTask.pinned)
				this.$eventBus.emit('updateTask', this.thisTask)
			} catch (err) {
				this.$toast.error(`Task ${this.thisTask.pinned ? 'pin' : 'unpin'} unsuccessfull`)
				this.thisTask.pinned = !this.thisTask.pinned 
				console.error(err)
			}
		},

		// Setup

		async loadTimes(initialLoad) {
			try {
				const currentPage = Math.ceil(this.times.length / 25) + 1
				if (this.isUnassigned || this.pendingTimes || currentPage > this.lastTimesPage) // vyňal by som logiku do premennej
					return

				if (initialLoad)
					this.times = initialLoad

				const { data, meta } = await (this.pendingTimes = api.loadTimes(this.thisTask._id, currentPage, 25))
				this.pendingTimes = null
				
				if (initialLoad) 
					this.times = data
				else
					this.times.push(...data)

				this.lastTimesPage ??= meta.last_page
			}
			catch (err) {
				this.pendingTimes = null
				console.error(err)
			}
		},

		editTime(i, newTime) {
			const timeDiff = newTime.duration - this.times[i].duration
			this.times[i] = newTime
			this.times.sort((a, b) => a.start > b.start ? -1 : (a.start < b.start ? 1 : 0))

			this.thisTask.totalTime += timeDiff 
		},

		deleteTime(i, time) {
			this.times.splice(i, 1)

			this.thisTask.totalTime -= time.duration 
		},

		// projects selecting

		async openProjectsSelectModal() {
			if (this.isUnassigned)
				return

			const projectModal = await this.$modals.open(TaskProjectSelect, null, { handle: false })

			const { data } = await projectModal.onDidDismiss()
			if (data)
				this._onProjectSelected(data)
		},

		async _onProjectSelected(projectSelection) {
			if (this.thisTask.projectId == projectSelection._id) return

			try {
				const updatedTask = await api.updateTask(this.thisTask._id, { projectId: projectSelection._id })
				this.projectChoice = projectSelection

				this.$eventBus.emit('removeTask', this.thisTask)
				this.thisTask.projectId = this.projectChoice._id 
				if (this.isActive)
					this.getActiveTask.projectId = this.projectChoice._id

				this.$eventBus.emit('updateTask', updatedTask)

			} catch (error) {
				console.error(error)
			}
		},

		// Tracking

		async setTracking(start) {
			if (start)
				await this.startTracking(this.thisTask)
			else
				await this.stopTrackingAction()
			
			this.closeModal()
		},

		// Name Edit

		async editNameAlertPrompt() {
			const inputs = [
				{ name: 'name', type: 'text', value: this.thisTask.name, id: 'popup-prompt-input' }
			]

			this.$popups.prompt('', 'Edit name', inputs, 'Save', date => this._saveNameEdit(date.name.trim()))
		},

		async _saveNameEdit(name) {
			if (name.length < 1 || this.thisTask.name == name) 
				return

			try {
				await api.updateTask(this.thisTask._id, { name })
				this.thisTask.name = name 
				this.$eventBus.emit('updateTask', this.thisTask)
			} catch (err) {
				console.error(err)
			}
		},

		// Time Edit

		async openAddTimeModal(date, firstTimeOfDate) {
			const comment = firstTimeOfDate?.comment
			const newTime = await openTimestampModal({ date, noBillableToggle: !this.projectChoice.is_billable, billable: this.thisTask.globalBillable, comment }, 'Add time', null)
			if (newTime)
				await this._postTimeEntry(newTime.start, newTime.end, newTime.comment, newTime.billable)
		},

		async _postTimeEntry(start, end, comment, billable) {
			await this.$wLoader.loadAction(async () => {
				try {
					const newTime = await api.addTime(this.thisTask._id, this.thisTask.projectId, start, end, comment, billable)
					this.thisTask.totalTime += newTime.duration 

					this.times.unshift(newTime)
					this.times.sort((a, b) => a.start > b.start ? -1 : (a.start < b.start ? 1 : 0))
				}
				catch (err) {
					console.error(err)
				}
			}, 'Adding time entry...')
		},

		async toggleCompleteTask() {
			try {
				const updatedTask = await api.updateTask(this.thisTask._id, { completed: !this.thisTask.completed })
				this.$eventBus.emit('updateTask', updatedTask)

				this.closeModal()
			}
			catch (err) {
				console.error(err)
			}
		},

		async updateCategory(ev) {
			try {
				const oldValue = this.thisTask.category
				const updatedTask = await api.updateTask(this.thisTask._id, { categoryId: ev.target.value })

				this.thisTask.emptyCategoryAlert = updatedTask.emptyCategoryAlert
				this.thisTask.category = updatedTask.category 
				this.$eventBus.emit('updatedCategory', { task: this.thisTask, oldValue, newValue: this.thisTask.category })
			}
			catch (err) {
				console.error(err)
			}
		},

		clearCategory() {
			this.updateCategory({ target: { value: 0 } })
		},

		async switchBillableForAll() {
			try {
				const confirm = await this.$popups.async.confirm(`This will switch billability for all times in this.thisTask to <b>${this.thisTask.globalBillable ? 'unbillable' : 'billable'}</b>!`, 'Do you want to switch?', 'Yes')
				if (!confirm)
					return

				await api.switchBillableForAll(this.thisTask._id, !this.thisTask.globalBillable)

				this.thisTask.globalBillable ^= true 
				this.times.forEach(time => time.billable = this.thisTask.globalBillable)
			}
			catch (e) {
				console.error(e)
			}
		},

		async getCategories() {
			try {
				if (this.thisTask.categories?.length)
					return

				if (this.thisTask.category)
					this.thisTask.categories = [this.thisTask.category] 
				this.thisTask.categories = await api.getCategories(this.thisTask.projectId) 
			}
			catch (err) {
				console.error(err)
			}
		},

		closeModal() {
			this.$modals.dismiss(this)
		},

		// notion data

		async getNotionTask() {
			try {
				const response = await api.getNotionTask(this.thisTask.notionTask._id)
				const notion_statuses = this.thisTask?.notionTask?.notion_statuses
				this.thisTask.notionTask = response
				this.thisTask.notionTask.notion_statuses = notion_statuses
			} catch (error) {
				console.error(error)
			}
		},

		async getNotionStatuses() {
			try {
				if (this.thisTask.notionTask.notion_statuses) return
				const response = await api.getNotionStatuses()
				this.thisTask.notionTask.notion_statuses = response
			} catch (error) {
				console.error(error)
			}
		},

		async updateNotionStatus(ev) {
			const oldStatus = this.thisTask.notionTask.notion_status
			this.thisTask.notionTask.notion_status = this.thisTask.notionTask.notion_statuses.find(status => status._id == ev.target.value)
			try {
				const response = await api.updateTask(this.thisTask._id, { status: ev.target.value })

				if (this.$refs['notion-status'].value === response.notionTask.notion_status._id)
					this.thisTask.notionTask.notion_status = response.notionTask.notion_status
			} catch (error) {
				console.error(error)
				this.thisTask.notionTask.notion_status = oldStatus
			}
		},

		async updateNotionProgress(ev) {
			const oldValue = this.thisTask.notionTask.notion_progress
			try {
				this.thisTask.notionTask.notion_progress = ev.target.value
				
				const response = await api.updateTask(this.thisTask._id, { progress: ev.target.value })
				if (this.$refs['notion-progress'].value === response.notionTask.notion_progress)
					this.thisTask.notionTask.notion_progress = response.notionTask.notion_progress
			} catch (error) {
				console.error(error)
				this.thisTask.notionTask.notion_progress = oldValue
			}
		},

		togglePicker(ev, isOpen) {
			console.log(this.$refs)
			this.$refs.plannedTimePicker.present()
			if (this.isPicker == isOpen) {
				this.clickEvent = ev
			}
		},

		updatePlannedTime(event) {
			let value = event.target.value
			let regex = /([0-9]+)h (0?[0-9]|[1-5][0-9])m/i
			if (!regex.test(value)) {
				return
			} else {
				let [ hours, minutes ] = regex.exec(value).splice(1);
				const totalSeconds = (Number(hours) * 3600) + (Number(minutes) * 60)
				return this.plannedTime = totalSeconds
			}
			
		},

		async sendUpdatePlannedTime() {
			this.isPicker = false
			try {
				const response = await api.updateTask(this.thisTask._id, { plannedTime: this.plannedTime })
				this.thisTask.plannedTime = response.plannedTime
				this.plannedTimeCopy = response.plannedTime
			} catch (error) {
				console.error(error)
				this.plannedTime = this.thisTask.plannedTime
				this.plannedTimeCopy = this.thisTask.plannedTime
			}
		},

		editedNote(event) {
			let note = event.target.value
			if (note.length < 1 || this.thisTask.note == note) 
				return
			this.thisTask.note = note
		},

		// Note edit
		async saveNoteEdit() {
			try {
				await api.updateTask(this.thisTask._id, { note: this.thisTask.note })
				// this.thisTask.note = name // might introduce same problem as in https://gitlab.com/wezeo/play/teamgrid-app/-/merge_requests/45
				this.$eventBus.emit('updateTask', this.thisTask)
			} catch (err) {
				console.error(err)
			}
		},

		async saveEdit() {
			await this.saveNoteEdit()
			await this.sendUpdatePlannedTime()
		}
	},
}
</script>

<style lang="sass" scoped>
@import '../../_theme/billable-btn.sass'

[theme="dark"]
	.pin-icon
		img
			filter: invert(1)

.content
	box-shadow: inset 0 6px 8px -8px rgb(0 0 0 / 30%)
	padding: 0 13px !important
	overflow-x: hidden
	overflow-y: auto

.times-display
	margin-bottom: 5px

	ion-input
		width: 85px
		font-size: 23px

		&:not(.has-focus)
			border: none !important

.row
	display: flex
	align-items: center
	justify-content: space-between
	margin-bottom: 10px

	div
		min-width: 49%

		&:nth-child(2)
			margin-left: 2%

		ion-select, ion-input
			border: none
			border-bottom: solid 1px var(--ion-color-medium-tint)
			border-radius: 0
			--padding-start: 3px
			height: 30px
			font-size: 16px

		.notion-status
			--padding-start: 22px

		.notion-status-dot
			min-width: unset !important
			width: 12px
			height: 12px
			border-radius: 6px
			top: 58%
			left: 3px

		ion-label
			margin-left: 3px
			font-size: 14px
			color: var(--ion-color-medium)
		
		.notion-progress-label
			position: absolute
			top: 49%
			left: 39px
			color: var(--ion-color-medium)

		.clear-icon
			top: 55%
			right: 8px
			z-index: 2
			color: #ababab

		.pencil-icon
			position: absolute
			right: 3px
			top: 55%
			color: var(--ion-color-medium)

	.billable
		position: relative
		top: 8px

.add-button
	margin: 0
	color: var(--ion-color-primary)
	font-weight: bold
	font-size: 16px
	cursor: pointer

h2
	font-size: 18px
	color: var(--ion-color-medium)
	margin-top: 0
	line-height: 18px

h3
	line-height: 23px

h4
	margin: 0

.categories ion-select, .notion-progress-select
	border: solid 1px var(--ion-color-medium-tint)
	border-radius: 5px

	&::part(icon)
		position: relative
		right: 22px

.project-image
	width: 55px
	height: 55px
	object-fit: cover
	border-radius: 100px
	border: 1px solid var(--ion-color-medium-tint)

.project-info
	--background: transparent !important
	margin: 0

	.project-select-icon
		font-size: 25px
		color: var(--ion-color-medium)

.customer
	color: var(--ion-color-medium-tint)

.complete-btn
	--color: #0449FF !important
	min-height: 40px
	margin-bottom: 0px !important
	--background: #ffffff !important
	--ion-color-base: none !important
	--border-style: solid
	--border-color: #0449FF
	--border-width: 1.5px

	ion-label
		color: #0449FF !important

ion-icon
	cursor: pointer

	&:before
		content: ''
		position: absolute
		width: 100%
		height: 100%

.pin-icon
	img
		height: 25px
		width: auto
		cursor: pointer

ion-textarea
	border: none
	border-bottom: solid 1px var(--ion-color-medium-tint)
	border-radius: 0
</style>
